import appConfig from '../../configs/appConfig';
import deviceImagesConfig from '../../configs/deviceImagesConfig';
import damBackendTypes from '../../consts/dam/damBackendTypes';
import deviceScreenTypes from '../../consts/device/deviceScreenTypes';
import deviceTypes from '../../consts/device/deviceTypes';
import DEVICE_TYPES_DATA from '../../consts/device/deviceTypesData';
import deviceConnectionStatusService from './deviceConnectionStatusService';

const deviceBatteryChargeValueValidation = (batteryCharge) => {
    if (isNaN(batteryCharge)) return;

    return Math.min(100, Math.max(0, batteryCharge));
};

const getDeviceTypeBySerialNumber = (serialNumber) => {
    if (!serialNumber) return null;

    const getParsedCodeFromSerialNumber = (start, end) => parseInt(serialNumber.substring(start, end), 16);
    const platformId = getParsedCodeFromSerialNumber(2, 6);
    const productId = getParsedCodeFromSerialNumber(6, 10);

    if ((productId === 0x0074 && platformId === 0x003c) || (productId === 0x003c && platformId === 0x0074)) {
        // 0x003c & 0x0074 - Devices with mixed-up productId and platformId
        return deviceTypes.ILUMA_I2_CHARGER;
    } else if (productId === 0x0066 && platformId === 0x0039) {
        return deviceTypes.ILUMA_I2_HOLDER;
    } else if (productId === 0x0075 && platformId === 0x003d) {
        return deviceTypes.ILUMA_I5_CHARGER;
    } else if ((productId === 0x0076 && platformId === 0x003e) || (productId === 0x007d && platformId === 0x0043)) {
        // 0x0076 & 0x003e - POC devices, just for internal use
        return deviceTypes.ILUMA_I5_HOLDER;
    } else {
        return appConfig.getDefaultDeviceType(); // Temporary fallback
    }
};

const getChargerImageSrc = (deviceType, deviceScreenType) => {
    const deviceConfig = deviceImagesConfig[deviceType];

    return deviceConfig?.[deviceScreenType] || deviceConfig?.[deviceScreenTypes.DEFAULT];
};

const getHolderImageSrc = (deviceType) => {
    const holderType = DEVICE_TYPES_DATA[deviceType]?.holderType || deviceType;
    const deviceConfig = deviceImagesConfig[holderType];

    return deviceConfig?.[deviceScreenTypes.DEFAULT];
};

const getIsIlumaI2 = (deviceType) =>
    deviceType === deviceTypes.ILUMA_I2_CHARGER || deviceType === deviceTypes.ILUMA_I2_HOLDER;

const getIsIlumaI5 = (deviceType) =>
    deviceType === deviceTypes.ILUMA_I5_CHARGER || deviceType === deviceTypes.ILUMA_I5_HOLDER;

const getDeviceDataByType = (deviceType) => DEVICE_TYPES_DATA[deviceType] || {};

const getIsUamf = (serialNumber) => {
    const deviceType = getDeviceTypeBySerialNumber(serialNumber);

    return getDeviceDataByType(deviceType)?.backendType === damBackendTypes.UAMF;
};

const getIsIlumaI5ByDusn = (dusn) => getIsIlumaI5(getDeviceTypeBySerialNumber(dusn));

const getSortedChargers = (devices) => {
    const defaultDeviceType = appConfig.getDefaultDeviceType();

    const getPriority = (device) => {
        const isConnected = deviceConnectionStatusService.isDeviceConnected(device?.connectionStatus);
        const isDefaultType = device?.type === defaultDeviceType;

        if (isConnected && isDefaultType) return 0;
        if (isDefaultType) return 1;
        if (isConnected) return 2;

        return 3;
    };

    return Object.values(devices || {})
        .filter((device) => !device?.isHolder)
        .sort((a, b) => getPriority(a) - getPriority(b));
};

export default {
    deviceBatteryChargeValueValidation,
    getDeviceTypeBySerialNumber,
    getChargerImageSrc,
    getHolderImageSrc,
    getIsIlumaI2,
    getIsIlumaI5,
    getDeviceDataByType,
    getIsUamf,
    getIsIlumaI5ByDusn,
    getSortedChargers,
};
